const CHANNEL = "digital-twin-viewer"
const VERSION = "1.0"
const VIEWER_SOURCE = "digital-twin-viewer-iframe"
const SDK_SOURCE = "digital-twin-viewer-sdk"

const safeOrigin = (value) => {
  if (typeof value !== "string" || value.trim().length === 0) return null

  try {
    return new URL(value).origin
  } catch (_error) {
    return null
  }
}

const targetOriginFromLocation = () => {
  const currentUrl = new URL(window.location.href)
  const requestedOrigin = safeOrigin(currentUrl.searchParams.get("sdk_origin"))
  if (requestedOrigin) return requestedOrigin

  const referrerOrigin = safeOrigin(document.referrer)
  if (referrerOrigin) return referrerOrigin

  return "*"
}

const viewerIdFromLocation = () => {
  const currentUrl = new URL(window.location.href)
  return currentUrl.searchParams.get("viewer_id") || "viewer"
}

const baseMessage = (viewerId) => ({
  channel: CHANNEL,
  version: VERSION,
  viewerId
})

export const createEmbedBridge = ({
  enabled = false,
  getSnapshot,
  focusObject,
  setViewMode,
  fitView,
  resetView,
  reload
}) => {
  if (!enabled || window.parent === window) {
    return {
      enabled: false,
      notifyReady() {},
      notifyStateChanged() {},
      notifySelectionChanged() {},
      notifyError() {},
      destroy() {}
    }
  }

  const viewerId = viewerIdFromLocation()
  const targetOrigin = targetOriginFromLocation()

  const postToParent = (payload) => {
    window.parent.postMessage(
      {
        ...baseMessage(viewerId),
        source: VIEWER_SOURCE,
        ...payload
      },
      targetOrigin
    )
  }

  const respond = (requestId, ok, payload = {}, error = null) => {
    postToParent({
      type: "response",
      requestId,
      ok,
      payload,
      error
    })
  }

  const notifyReady = (payload = getSnapshot()) => {
    postToParent({
      type: "event",
      event: "ready",
      payload
    })
  }

  const notifyStateChanged = (payload = getSnapshot()) => {
    postToParent({
      type: "event",
      event: "stateChanged",
      payload
    })
  }

  const notifySelectionChanged = (selection = null, snapshot = getSnapshot()) => {
    postToParent({
      type: "event",
      event: "selectionChanged",
      payload: {
        selection,
        snapshot
      }
    })
  }

  const notifyError = (message, meta = {}) => {
    postToParent({
      type: "event",
      event: "error",
      payload: {
        message,
        ...meta
      }
    })
  }

  const handleCommand = async (command, payload = {}) => {
    switch (command) {
      case "ping":
        return { ok: true, payload: { viewerId, version: VERSION } }
      case "getState":
        return { ok: true, payload: getSnapshot() }
      case "setViewMode":
        await setViewMode(payload)
        return { ok: true, payload: getSnapshot() }
      case "fitView":
        await fitView()
        return { ok: true, payload: getSnapshot() }
      case "resetView":
        await resetView()
        return { ok: true, payload: getSnapshot() }
      case "focusObject":
        return { ok: true, payload: await focusObject(payload) }
      case "reload":
        await reload()
        return { ok: true, payload: { reloading: true } }
      default:
        throw new Error(`Unsupported embedded viewer command: ${command}`)
    }
  }

  const handleMessage = async (event) => {
    if (event.source !== window.parent) return
    if (targetOrigin !== "*" && event.origin !== targetOrigin) return

    const message = event.data
    if (!message || typeof message !== "object") return
    if (message.channel !== CHANNEL || message.version !== VERSION) return
    if (message.source !== SDK_SOURCE || message.viewerId !== viewerId) return
    if (message.type !== "command" || !message.command) return

    try {
      const result = await handleCommand(message.command, message.payload || {})
      respond(message.requestId, result.ok, result.payload || {})
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      respond(message.requestId, false, {}, { message: errorMessage })
      notifyError(errorMessage, { command: message.command })
    }
  }

  window.addEventListener("message", handleMessage)

  return {
    enabled: true,
    notifyReady,
    notifyStateChanged,
    notifySelectionChanged,
    notifyError,
    destroy() {
      window.removeEventListener("message", handleMessage)
    }
  }
}

export const EMBED_BRIDGE_VERSION = VERSION
